"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = __importDefault(require("axios"));
const find_1 = __importDefault(require("lodash/find"));
const values_1 = __importDefault(require("lodash/values"));
const constants_1 = require("../constants");
const cookie_service_1 = __importDefault(require("../services/cookie.service"));
const isClient_1 = __importDefault(require("../utils/ssr/isClient"));
class HttpService {
    constructor() {
        this.pendingGroups = {};
        this.pendingRequests = {};
        /**
         * formats endpoint url
         * @param endpoint {string}
         * @returns {string}
         */
        this.formatEndpoint = (endpoint) => {
            return encodeURI(endpoint);
        };
        /**
         * formats base url. adds to axios instances
         * @param url
         */
        this.formatBaseUrl = (url) => {
            this.httpClient.defaults.baseURL = url;
        };
        /**
         * add endpoint formatter
         * @param {function} handler [takes `url` and returns `url`]
         */
        this.addEndpointFormatter = (newHandler) => {
            if (newHandler) {
                this.formatEndpoint = newHandler;
            }
        };
        /**
         * Add HTTP headers, other than the defaults
         * @param headers {object}
         */
        this.addHeaders = (headers) => {
            this.httpClient.defaults.headers.common = Object.assign(Object.assign({}, this.httpClient.defaults.headers.common), headers);
        };
        /**
         * handle requests group cancellation
         *
         * @description
         * this method handles a group of requests cancellation
         * if a new a group of requests made with the same group name
         * it can be used for polling cases
         *
         * ex: $http.get(requestId, endpoint, { groupName: 'ASYNC_SEARCH', groupId: sId }).then(({ data }) =>  data);
         *
         * @param {String} groupName [fixed for the new requests and old requests]
         * @param {String} groupId [unique between old requests and new requests]
         * @param {Function} canceller [axios cancel handler]
         * @param {Object} pendingGroups [this.pendingGroups]
         * @return {Object} new pendingGroups
         */
        this.handleRequestsGroupCancellation = ({ groupName, groupId, canceller, pendingGroups, }) => {
            var _a, _b, _c, _d, _e, _f;
            if (!groupName || !groupId)
                return pendingGroups;
            const newPendingGroups = Object.assign({}, pendingGroups);
            const isGroupExist = groupName in newPendingGroups;
            const isNewGroupId = !(0, find_1.default)((0, values_1.default)(newPendingGroups[groupName]), {
                id: groupId,
            });
            const isActiveGroupId = isGroupExist && groupId === ((_a = newPendingGroups[groupName].active) === null || _a === void 0 ? void 0 : _a.id);
            const isOldGroupId = isGroupExist && groupId === ((_b = newPendingGroups[groupName].old) === null || _b === void 0 ? void 0 : _b.id);
            switch (true) {
                case !isGroupExist:
                    newPendingGroups[groupName] = {
                        active: { id: groupId, requests: [canceller] },
                        old: { id: null, requests: [] },
                    };
                    break;
                case isActiveGroupId:
                    (_d = (_c = newPendingGroups[groupName].active) === null || _c === void 0 ? void 0 : _c.requests) === null || _d === void 0 ? void 0 : _d.push(canceller);
                    break;
                case isOldGroupId:
                    canceller();
                    break;
                case isNewGroupId:
                    newPendingGroups[groupName] = {
                        active: { id: groupId, requests: [canceller] },
                        old: Object.assign({}, newPendingGroups[groupName].active),
                    };
                    (_f = (_e = newPendingGroups[groupName].old) === null || _e === void 0 ? void 0 : _e.requests) === null || _f === void 0 ? void 0 : _f.forEach((cancellerIns) => cancellerIns());
                    break;
                default:
                    break;
            }
            return newPendingGroups;
        };
        /**
         * get
         * @description an http 'GET' request method function signature
         */
        this.get = this.request('get');
        /**
         * get
         * @description an http 'POST' request method function signature
         */
        this.post = this.request('post');
        /**
         * get
         * @description an http 'PUT' request method function signature
         */
        this.put = this.request('put');
        /**
         * get
         * @description an http 'DELETE' request method function signature
         */
        this.delete = this.request('delete');
        this.all = Promise.all;
        this.httpClient = axios_1.default.create({
            adapter: 'fetch',
            // baseURL: '',
            headers: {
                'Content-Type': 'application/json; charset=UTF-8',
                'Cache-Control': 'max-age=86400',
                Accept: 'application/json',
                'Accept-Language': cookie_service_1.default.get(constants_1.STORAGE_KEYS.LANG_COOKIE) || constants_1.LANG.EN,
            },
        });
    }
    /**
     * remove headers
     * @param {Array<string>} headersKeys
     */
    removeHeaders(headersKeys) {
        headersKeys.forEach((key) => {
            delete this.httpClient.defaults.headers.common[key];
        });
    }
    /**
     * cancel pending requests
     * @param requestId {string}
     */
    cancelPendingRequest(requestId) {
        if (requestId in this.pendingRequests) {
            this.pendingRequests[requestId]();
            delete this.pendingRequests[requestId];
        }
    }
    /**
     * cancels all pending requests
     */
    cancelAllPendingRequests() {
        Object.keys(this.pendingRequests).forEach((requestId) => {
            this.cancelPendingRequest(requestId);
        });
    }
    /**
     * get request payload
     * @param method {string}
     * @param endpoint {string}
     * @param config {object}
     */
    getRequestPayload(method, endpoint, config = {}) {
        const url = this.formatEndpoint(endpoint);
        console.log('** url: ', url);
        return Object.assign({ url,
            method }, config);
    }
    /**
     * make request method
     * @param {string} method
     * @return {RequestMethod}
     */
    request(method) {
        return (requestId, endpoint, config) => {
            if ((0, isClient_1.default)()) {
                // removing this cancel call, as it seems buggy
                // this.cancelPendingRequest(requestId);
            }
            const requestPayload = this.getRequestPayload(method, endpoint, config);
            if ((0, isClient_1.default)()) {
                requestPayload.cancelToken = new axios_1.default.CancelToken((canceller) => {
                    this.pendingRequests[requestId] = canceller;
                    if (typeof requestPayload.groupId === 'string' &&
                        typeof requestPayload.groupName === 'string') {
                        this.pendingGroups = this.handleRequestsGroupCancellation({
                            groupId: requestPayload.groupId,
                            groupName: requestPayload.groupName,
                            pendingGroups: this.pendingGroups,
                            canceller,
                        });
                    }
                });
            }
            console.log('** requestPayload: ', requestPayload);
            return this.httpClient.request(requestPayload);
        };
    }
}
const $http = new HttpService();
exports.default = $http;
